import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { OFFERING_CATEGORIES } from "@/data/courses";
import { CATEGORY_EXPLORER } from "@/constants/testIds";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";

export default function CategoryExplorer() {
  const [active, setActive] = useState(OFFERING_CATEGORIES[0].id);
  const current = OFFERING_CATEGORIES.find((c) => c.id === active);

  return (
    <section
      data-testid={CATEGORY_EXPLORER.root}
      id="categories"
      className="relative py-12 lg:py-12 bg-bg-alt"
    >
      <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-end">
          <div className="lg:col-span-7">
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-royal">
              01 · What you can learn
            </p>
            <h2 className="font-display text-2xl md:text-3xl lg:text-[34px] font-bold text-ink mt-3 leading-[1.1] text-balance">
              What ZaranTech helps you learn , 
              <br className="hidden md:block" />
              <span className="text-royal-deep">across SAP, Workday, AI and leadership.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <p className="text-ink-soft text-[15px] max-w-md lg:ml-auto leading-relaxed">
              Pick a category to see who it&apos;s for, what&apos;s covered, and how it connects to real
              enterprise work.
            </p>
          </div>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-6 lg:gap-10">
          {/* Tabs */}
          <div className="lg:col-span-4">
            <ul className="rounded-2xl border border-line bg-white overflow-hidden">
              {OFFERING_CATEGORIES.map((c, idx) => {
                const isActive = c.id === active;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      data-testid={CATEGORY_EXPLORER.tab(c.id)}
                      onClick={() => {
                        setActive(c.id);
                        trackEvent(ANALYTICS_EVENTS.CATEGORY_SELECTED, { id: c.id });
                      }}
                      className={`w-full text-left px-5 py-4 flex items-start gap-3 transition-colors ${
                        isActive ? "bg-cyan-soft" : "hover:bg-bg-alt"
                      } ${idx > 0 ? "border-t border-line" : ""}`}
                    >
                      <span
                        className={`mt-1 inline-block h-1.5 w-1.5 rounded-full ${
                          isActive ? "bg-[var(--cyan-deep)]" : "bg-ink/20"
                        }`}
                        aria-hidden
                      />
                      <span className="flex-1">
                        <span
                          className={`block font-display text-[15px] font-semibold ${
                            isActive ? "text-royal-deep" : "text-ink"
                          }`}
                        >
                          {c.title}
                        </span>
                        <span className="block text-[12.5px] text-ink-soft mt-1 leading-snug">
                          {c.short}
                        </span>
                      </span>
                      <ArrowRight
                        size={14}
                        className={`mt-1 transition-transform ${
                          isActive ? "text-royal-deep translate-x-0.5" : "text-ink-soft/40"
                        }`}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Panel */}
          <div className="lg:col-span-8">
            <div
              key={current.id}
              data-testid={CATEGORY_EXPLORER.panel}
              className="zt-fade rounded-2xl border border-line bg-white p-7 lg:p-10 relative overflow-hidden"
            >
              <div
                className="absolute -right-24 -top-24 h-72 w-72 rounded-full opacity-[0.08]"
                style={{ background: "var(--royal)" }}
                aria-hidden
              />
              <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-royal">
                {current.title}
              </p>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-ink mt-3 leading-tight max-w-xl text-balance">
                {current.longCopy}
              </h3>

              <div className="mt-7 grid md:grid-cols-2 gap-6">
                <div>
                  <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-ink-soft mb-3">
                    Who it&apos;s for
                  </p>
                  <ul className="space-y-2">
                    {current.audience.map((a) => (
                      <li key={a} className="flex items-start gap-2 text-[14px] text-ink">
                        <Check size={14} className="mt-1 text-[var(--cyan-deep)]" />
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-ink-soft mb-3">
                    Example topics
                  </p>
                  <ul className="space-y-2">
                    {current.topics.map((t) => (
                      <li key={t} className="flex items-start gap-2 text-[14px] text-ink">
                        <span className="mt-2 h-1 w-3 rounded-full bg-royal" aria-hidden />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to={current.link}
                  data-testid={CATEGORY_EXPLORER.cta}
                  className="inline-flex items-center gap-2 rounded-md bg-royal-deep px-5 py-3 text-sm font-semibold text-white hover:bg-royal transition-colors"
                >
                  {current.cta} <ArrowRight size={14} />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-md border border-line bg-white px-5 py-3 text-sm font-semibold text-ink hover:border-royal hover:text-royal"
                >
                  Talk to Advisor
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
